import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceTextarea } from "@/components/VoiceInput";
import { Switch } from "@/components/ui/switch";
import ResponsiveSignaturePad from "@/components/ResponsiveSignaturePad";
import {
    ArrowLeft, Truck, Check, X, Plus, Trash2, Save, ChevronDown, ChevronUp, Search, Sparkles, Loader2, QrCode,
} from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const VEHICLE_TYPE_LABELS = {
    camion: "Camión", panel: "Panel", pickup: "Pickup",
    furgon: "Furgón", cisterna: "Cisterna", otro: "Otro",
};

const VEHICLE_TYPES = [
    { value: "camion", label: "Camión" },
    { value: "panel", label: "Panel" },
    { value: "pickup", label: "Pickup" },
    { value: "furgon", label: "Furgón" },
    { value: "cisterna", label: "Cisterna" },
    { value: "otro", label: "Otro" },
];

export default function TechTransportService() {
    const { id: serviceId } = useParams();
    const nav = useNavigate();
    const _loc = useLocation();
    const basePath = _loc.pathname.startsWith("/audit") ? "/audit" : "/tech";
    const [loading, setLoading] = useState(true);
    const [service, setService] = useState(null);
    const [client, setClient] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [performedBy, setPerformedBy] = useState("");
    const [acceptedBy, setAcceptedBy] = useState("");
    const [generalObs, setGeneralObs] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [expandedVehicle, setExpandedVehicle] = useState(null);
    const [existingReport, setExistingReport] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddVehicle, setShowAddVehicle] = useState(false);
    const [newVehicle, setNewVehicle] = useState({ plate_number: "", brand: "", tonnage: "", vehicle_type: "camion", observations: "" });
    const [addingVehicle, setAddingVehicle] = useState(false);
    const [improvingObs, setImprovingObs] = useState(false);
    const [savedReport, setSavedReport] = useState(null);
    const sigRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            try {
                const svcRes = await api.get(`/schedules/${serviceId}`);
                const svc = svcRes.data;
                setService(svc);

                const [clientsRes, vehiclesRes, existingRes] = await Promise.all([
                    api.get("/clients"),
                    api.get(`/transport/vehicles?client_id=${svc.client_id}`),
                    api.get(`/transport/services?scheduled_service_id=${serviceId}`),
                ]);

                const c = clientsRes.data.find((x) => x.id === svc.client_id);
                setClient(c);
                setVehicles(vehiclesRes.data);

                // Check if transport service already exists for this visit
                if (existingRes.data.length > 0) {
                    const existing = existingRes.data[0];
                    setExistingReport(existing);
                    setPerformedBy(existing.performed_by || "");
                    setAcceptedBy(existing.accepted_by || "");
                    setGeneralObs(existing.general_observations || "");
                    // Reconstruct treatments from existing
                    const tMap = {};
                    for (const t of existing.treatments || []) {
                        tMap[t.vehicle_id] = t;
                    }
                    setTreatments(
                        vehiclesRes.data.map((v) => {
                            const ex = tMap[v.id];
                            return ex || {
                                vehicle_id: v.id,
                                plate_number: v.plate_number,
                                brand: v.brand,
                                tonnage: v.tonnage,
                                vehicle_type: v.vehicle_type,
                                service_done: false,
                                products_applied: [],
                                observations: "",
                            };
                        })
                    );
                } else {
                    // Init treatments from fleet — default to NOT treated
                    setTreatments(
                        vehiclesRes.data.map((v) => ({
                            vehicle_id: v.id,
                            plate_number: v.plate_number,
                            brand: v.brand,
                            tonnage: v.tonnage,
                            vehicle_type: v.vehicle_type,
                            service_done: false,
                            products_applied: [],
                            observations: "",
                        }))
                    );
                }

                // Pre-fill tech name
                try {
                    const techRes = await api.get("/technicians");
                    const tech = techRes.data.find((t) => t.id === svc.technician_id || t.user_id === svc.technician_id);
                    if (tech) setPerformedBy(tech.full_name);
                } catch { /* tech name optional */ }
            } catch (e) {
                toast.error("Error al cargar datos");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [serviceId]);

    const suppliesAsPresets = useMemo(() => {
        return (service?.supplies_assigned || [])
            .filter((s) => ["ASP", "POL"].includes((s.method || "").toUpperCase()))
            .map((s) => ({
                product_name: s.product,
                quantity: 0,
                unit: s.unit || "",
                method: s.method || "",
                dose: s.dose || "",
                is_preset: true,
            }));
    }, [service]);

    const toggleServiceDone = (vehicleId) => {
        setTreatments((prev) =>
            prev.map((t) => {
                if (t.vehicle_id !== vehicleId) return t;
                const newDone = !t.service_done;
                // Auto-populate products from supplies_assigned when toggling ON
                if (newDone && t.products_applied.length === 0 && suppliesAsPresets.length > 0) {
                    return { ...t, service_done: true, products_applied: suppliesAsPresets.map((p) => ({ ...p })) };
                }
                return { ...t, service_done: newDone };
            })
        );
    };

    const updateObs = (vehicleId, value) => {
        setTreatments((prev) =>
            prev.map((t) =>
                t.vehicle_id === vehicleId ? { ...t, observations: value } : t
            )
        );
    };

    const addProduct = (vehicleId) => {
        setTreatments((prev) =>
            prev.map((t) =>
                t.vehicle_id === vehicleId
                    ? { ...t, products_applied: [...t.products_applied, { product_name: "", quantity: null, unit: "", method: "" }] }
                    : t
            )
        );
    };

    const updateProduct = (vehicleId, idx, field, value) => {
        setTreatments((prev) =>
            prev.map((t) =>
                t.vehicle_id === vehicleId
                    ? {
                        ...t,
                        products_applied: t.products_applied.map((p, i) =>
                            i === idx ? { ...p, [field]: value } : p
                        ),
                    }
                    : t
            )
        );
    };

    const removeProduct = (vehicleId, idx) => {
        setTreatments((prev) =>
            prev.map((t) =>
                t.vehicle_id === vehicleId
                    ? { ...t, products_applied: t.products_applied.filter((_, i) => i !== idx) }
                    : t
            )
        );
    };

    const treatedCount = useMemo(() => treatments.filter((t) => t.service_done).length, [treatments]);

    const filteredTreatments = useMemo(() => {
        if (!searchQuery.trim()) return treatments;
        const q = searchQuery.toUpperCase().trim();
        return treatments.filter((t) =>
            t.plate_number.toUpperCase().includes(q) ||
            (t.brand || "").toUpperCase().includes(q)
        );
    }, [treatments, searchQuery]);

    const handleAddVehicle = async () => {
        if (!newVehicle.plate_number.trim()) {
            toast.error("La placa es obligatoria");
            return;
        }
        setAddingVehicle(true);
        try {
            const res = await api.post("/transport/report-vehicle", {
                client_id: service.client_id,
                plate_number: newVehicle.plate_number,
                brand: newVehicle.brand || undefined,
                tonnage: newVehicle.tonnage || undefined,
                vehicle_type: newVehicle.vehicle_type,
                observations: newVehicle.observations || undefined,
            });
            const data = res.data;

            // Open WhatsApp with pre-filled message
            const phone = (data.salesperson_phone || "").replace(/[^0-9+]/g, "");
            const waUrl = phone
                ? `https://wa.me/${phone}?text=${encodeURIComponent(data.whatsapp_message)}`
                : `https://wa.me/?text=${encodeURIComponent(data.whatsapp_message)}`;
            window.open(waUrl, "_blank");

            toast.success("Vehículo reportado. Se abrió WhatsApp para notificar al vendedor.");
            setShowAddVehicle(false);
            setNewVehicle({ plate_number: "", brand: "", tonnage: "", vehicle_type: "camion", observations: "" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al reportar vehículo");
        } finally {
            setAddingVehicle(false);
        }
    };

    const improveObsWithAI = async () => {
        if (!generalObs.trim()) return toast.error("Escribe algo primero para poder mejorarlo");
        setImprovingObs(true);
        const token = localStorage.getItem("trulyn_token");
        try {
            const resp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/ai/improve-text`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ field_type: "recommendations", text: generalObs }),
                }
            );
            if (!resp.ok) throw new Error("Error del servidor");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let accumulated = "";
            setGeneralObs("");
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data: ")) continue;
                    const raw = line.slice(6).trim();
                    if (raw === "[DONE]") break;
                    try {
                        const parsed = JSON.parse(raw);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.content) {
                            accumulated += parsed.content;
                            setGeneralObs(accumulated);
                        }
                    } catch (_e) { /* skip */ }
                }
            }
            toast.success("Texto mejorado con IA");
        } catch {
            toast.error("No se pudo mejorar el texto");
        } finally {
            setImprovingObs(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            let sigData = undefined;
            if (sigRef.current && !sigRef.current.isEmpty()) {
                sigData = sigRef.current.toDataURL("image/png");
            }
            const res = await api.post("/transport/services", {
                scheduled_service_id: serviceId,
                treatments,
                performed_by: performedBy,
                accepted_by: acceptedBy,
                accepted_by_signature_base64: sigData,
                general_observations: generalObs || undefined,
            });
            toast.success("Reporte de transportes guardado");
            setSavedReport(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-6 text-muted-foreground">Cargando...</div>;
    if (!service) return <div className="p-6 text-red-600">Servicio no encontrado</div>;

    if (vehicles.length === 0) {
        return (
            <div className="px-4 pt-4 pb-8">
                <Link to={`${basePath}/service/${serviceId}`} className="inline-flex items-center text-sm text-muted-foreground mb-3 hover:text-foreground">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Volver al servicio
                </Link>
                <Card className="p-8 text-center">
                    <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="font-semibold text-lg mb-1">Sin vehículos registrados</p>
                    <p className="text-sm text-muted-foreground">
                        Este cliente no tiene vehículos en su flota. El admin debe registrarlos primero.
                    </p>
                </Card>
            </div>
        );
    }

    return (
        <div className="px-4 pt-4 pb-32" data-testid="tech-transport-service">
            <Link
                to={`${basePath}/service/${serviceId}`}
                className="inline-flex items-center text-sm text-muted-foreground mb-3 hover:text-foreground"
                data-testid="transport-back-link"
            >
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver al servicio
            </Link>

            {/* Header */}
            <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg bg-[#CC0000]/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#CC0000]" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Control de Rastreros en Transportes
                    </h1>
                    <p className="text-sm text-muted-foreground">{client?.name}</p>
                </div>
            </div>

            {existingReport && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3 mt-3 text-xs text-amber-900">
                    Ya existe un reporte para esta visita. Al guardar se creará uno nuevo.
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 my-4">
                <Card className="p-3 text-center">
                    <div className="text-2xl font-bold">{vehicles.length}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                </Card>
                <Card className="p-3 text-center bg-green-50 border-green-200">
                    <div className="text-2xl font-bold text-green-700">{treatedCount}</div>
                    <div className="text-[10px] uppercase tracking-wider text-green-600">Tratados</div>
                </Card>
                <Card className="p-3 text-center bg-orange-50 border-orange-200">
                    <div className="text-2xl font-bold text-orange-600">{vehicles.length - treatedCount}</div>
                    <div className="text-[10px] uppercase tracking-wider text-orange-500">Sin tratar</div>
                </Card>
            </div>

            {/* Search + Add vehicle */}
            <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por placa o marca..."
                        className="pl-9 h-10"
                        data-testid="transport-search"
                    />
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 border-dashed border-[#CC0000]/40 text-[#CC0000]"
                    onClick={() => setShowAddVehicle(true)}
                    data-testid="transport-add-vehicle-btn"
                    title="Reportar nuevo vehículo"
                >
                    <Plus className="w-5 h-5" />
                </Button>
            </div>

            {searchQuery && (
                <div className="text-xs text-muted-foreground mb-2">
                    Mostrando {filteredTreatments.length} de {treatments.length} vehículos
                </div>
            )}

            {/* Vehicle list */}
            <div className="space-y-2 mb-4">
                {filteredTreatments.map((t) => {
                    const expanded = expandedVehicle === t.vehicle_id;
                    return (
                        <Card
                            key={t.vehicle_id}
                            className={`overflow-hidden transition-all ${t.service_done ? "border-green-200" : "border-orange-200"}`}
                            data-testid={`treatment-card-${t.vehicle_id}`}
                        >
                            {/* Vehicle header row */}
                            <div
                                className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                                onClick={() => setExpandedVehicle(expanded ? null : t.vehicle_id)}
                            >
                                {/* Toggle service done */}
                                <button
                                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                        t.service_done
                                            ? "bg-green-500 text-white"
                                            : "bg-gray-200 text-gray-400"
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); toggleServiceDone(t.vehicle_id); }}
                                    data-testid={`treatment-toggle-${t.vehicle_id}`}
                                >
                                    {t.service_done ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                </button>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono font-bold text-sm">{t.plate_number}</span>
                                        <Badge variant="outline" className="text-[10px]">
                                            {VEHICLE_TYPE_LABELS[t.vehicle_type] || t.vehicle_type}
                                        </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {t.brand}{t.tonnage ? ` · ${t.tonnage}` : ""}
                                    </div>
                                </div>

                                <Badge className={t.service_done ? "bg-green-100 text-green-800 border-green-200" : "bg-orange-100 text-orange-700 border-orange-200"}>
                                    {t.service_done ? "SI" : "NO"}
                                </Badge>

                                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                            </div>

                            {/* Expanded detail */}
                            {expanded && (
                                <div className="px-3 pb-3 pt-0 border-t space-y-3">
                                    {/* Products */}
                                    {t.products_applied.length > 0 && (
                                        <div className="mt-3 space-y-2.5">
                                            {t.products_applied.map((p, idx) => (
                                                    <div key={idx} className="border rounded-lg p-3 bg-zinc-50/60" data-testid={`preset-product-${t.vehicle_id}-${idx}`}>
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div>
                                                                <p className="font-semibold text-sm leading-tight">{p.product_name}</p>
                                                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                    {p.dose && (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">
                                                                            <span className="font-medium text-zinc-400">Dosis</span> {p.dose}
                                                                        </span>
                                                                    )}
                                                                    {p.method && (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">
                                                                            <span className="font-medium text-zinc-400">Método</span> {p.method}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Badge variant="outline" className="font-mono shrink-0">#{idx + 1}</Badge>
                                                        </div>
                                                        <div className="flex items-end gap-2">
                                                            <div className="flex-1">
                                                                <Label className="text-xs font-semibold">Cantidad utilizada <span className="text-red-500">*</span></Label>
                                                                <Input
                                                                    type="number"
                                                                    step="0.1"
                                                                    min="0"
                                                                    value={p.quantity || ""}
                                                                    onChange={(e) => updateProduct(t.vehicle_id, idx, "quantity", e.target.value === "" ? 0 : Number(e.target.value))}
                                                                    placeholder="0"
                                                                    className="h-11 mt-1 text-base font-semibold"
                                                                    data-testid={`product-qty-${t.vehicle_id}-${idx}`}
                                                                />
                                                            </div>
                                                            {p.unit && (
                                                                <div className="h-11 flex items-center px-3 rounded-md border bg-white text-sm font-semibold text-zinc-700 shrink-0">
                                                                    {p.unit}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Observations */}
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Observaciones</Label>
                                        <VoiceTextarea
                                            rows={2}
                                            value={t.observations || ""}
                                            onChange={(val) => updateObs(t.vehicle_id, val)}
                                            placeholder="Observaciones para este vehículo..."
                                            className="text-sm resize-none mt-1"
                                            testId={`obs-${t.vehicle_id}`}
                                        />
                                    </div>
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>

            {/* General observations */}
            <Card className="p-3 mb-3">
                <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs text-muted-foreground">Observaciones generales</Label>
                    <button
                        type="button"
                        onClick={improveObsWithAI}
                        disabled={improvingObs}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#FFD62B] bg-[#FFD62B]/10 text-black hover:bg-[#FFD62B]/30 disabled:opacity-50 transition-colors"
                        data-testid="ai-improve-general-obs"
                        title="Mejorar redacción con IA"
                    >
                        {improvingObs
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Mejorando...</>
                            : <><Sparkles className="w-3 h-3" /> Mejorar con IA</>
                        }
                    </button>
                </div>
                <VoiceTextarea
                    rows={2}
                    value={generalObs}
                    onChange={(val) => setGeneralObs(val)}
                    placeholder="Observaciones generales del servicio de transportes..."
                    className="text-sm resize-none mt-1"
                    testId="transport-general-obs"
                />
            </Card>

            {/* Signatures */}
            <Card className="p-3 mb-3">
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-xs text-muted-foreground">Realizado por</Label>
                        <Input
                            value={performedBy}
                            onChange={(e) => setPerformedBy(e.target.value)}
                            placeholder="Nombre del técnico"
                            className="h-9 text-sm mt-1"
                            data-testid="transport-performed-by"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Aceptado por</Label>
                        <Input
                            value={acceptedBy}
                            onChange={(e) => setAcceptedBy(e.target.value)}
                            placeholder="Nombre del cliente"
                            className="h-9 text-sm mt-1"
                            data-testid="transport-accepted-by"
                        />
                    </div>
                </div>
                <Label className="text-xs text-muted-foreground mb-1 block">Firma del cliente</Label>
                <ResponsiveSignaturePad
                    ref={sigRef}
                    data-testid="transport-signature"
                />
            </Card>

            {/* Sticky submit */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t z-20">
                <div className="max-w-2xl mx-auto px-4 py-3">
                    <Button
                        className="w-full h-12 bg-[#CC0000] hover:bg-[#CC0000]/90 text-white font-semibold text-base"
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="transport-submit"
                    >
                        <Save className="w-5 h-5 mr-2" />
                        {submitting ? "Guardando..." : `Guardar reporte (${treatedCount}/${vehicles.length} tratados)`}
                    </Button>
                </div>
            </div>

            {/* Report vehicle dialog */}
            <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-[#CC0000]" />
                            Reportar nuevo vehículo
                        </DialogTitle>
                        <p className="text-xs text-muted-foreground">Para agregar al proyecto en Odoo</p>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Número de placa *</Label>
                            <Input
                                value={newVehicle.plate_number}
                                onChange={(e) => setNewVehicle((f) => ({ ...f, plate_number: e.target.value }))}
                                placeholder="Ej: P 154 C6"
                                className="h-11"
                                data-testid="add-vehicle-plate"
                            />
                        </div>
                        <div>
                            <Label>Tipo de vehículo</Label>
                            <Select
                                value={newVehicle.vehicle_type}
                                onValueChange={(v) => setNewVehicle((f) => ({ ...f, vehicle_type: v }))}
                            >
                                <SelectTrigger className="h-11" data-testid="add-vehicle-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {VEHICLE_TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Marca</Label>
                                <Input
                                    value={newVehicle.brand}
                                    onChange={(e) => setNewVehicle((f) => ({ ...f, brand: e.target.value }))}
                                    placeholder="Ej: Chevrolet"
                                    className="h-11"
                                    data-testid="add-vehicle-brand"
                                />
                            </div>
                            <div>
                                <Label>Tonelaje</Label>
                                <Input
                                    value={newVehicle.tonnage}
                                    onChange={(e) => setNewVehicle((f) => ({ ...f, tonnage: e.target.value }))}
                                    placeholder="Ej: 5 ton"
                                    className="h-11"
                                    data-testid="add-vehicle-tonnage"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Observaciones o Comentarios</Label>
                            <Textarea
                                rows={2}
                                value={newVehicle.observations}
                                onChange={(e) => setNewVehicle((f) => ({ ...f, observations: e.target.value }))}
                                placeholder="Información adicional sobre el vehículo..."
                                className="text-sm resize-none"
                                data-testid="add-vehicle-observations"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddVehicle(false)}>Cancelar</Button>
                        <Button
                            onClick={handleAddVehicle}
                            disabled={addingVehicle || !newVehicle.plate_number.trim()}
                            className="bg-[#CC0000] hover:bg-[#CC0000]/90 text-white"
                            data-testid="add-vehicle-submit"
                        >
                            {addingVehicle ? "Notificando..." : "Notificar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR Success Dialog */}
            <Dialog open={!!savedReport} onOpenChange={() => {}}>
                <DialogContent className="text-center">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-center gap-2">
                            <Check className="w-5 h-5 text-green-600" /> Reporte guardado
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        {savedReport?.qr_report_url && (
                            <img
                                src={assetUrl(savedReport.qr_report_url)}
                                alt="QR"
                                className="w-40 h-40 mx-auto mb-3"
                                data-testid="transport-qr-code"
                            />
                        )}
                        <p className="text-sm text-muted-foreground mb-1">Código de acceso</p>
                        <p className="text-3xl font-bold font-mono tracking-widest mb-3" data-testid="transport-pin-display">
                            {savedReport?.qr_access_pin || "—"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            El cliente puede escanear el QR para ver el reporte digital
                        </p>
                    </div>
                    <DialogFooter className="justify-center">
                        <Button
                            className="bg-[#CC0000] hover:bg-[#CC0000]/90 text-white w-full"
                            onClick={() => nav(`${basePath}/service/${serviceId}`)}
                            data-testid="transport-qr-done"
                        >
                            Listo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
